import type { QuestionWithSuggestions } from '../api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDeepSearchQuestionsMutation } from '../api';

interface UseQuestionContextProps {
  pushLoadingState: () => void;
  pushSystemMessage: (message: string | React.ReactNode) => void;
  onComplete: ({
    topic,
    questions,
    answers,
  }: {
    topic: string;
    questions: string[];
    answers: Record<number, string>;
  }) => void;
}

export const useQuestionContext = ({ pushLoadingState, pushSystemMessage, onComplete }: UseQuestionContextProps) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [topic, setTopic] = useState<string | undefined>(undefined);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [questionsWithSuggestions, setQuestionsWithSuggestions] = useState<QuestionWithSuggestions[]>([]);
  const [placeholder, setPlaceholder] = useState('What topic would you like to create training for?');
  const [currentSuggestions, setCurrentSuggestions] = useState<string[]>([]);

  const isInitialized = useRef(false);

  const deepSearchQuestionsMutation = useDeepSearchQuestionsMutation();

  const handleUserMessage = useCallback(
    async (message: string) => {
      pushLoadingState();
      if (currentQuestionIndex === -1) {
        setTopic(message);
        setPlaceholder('Type your answer here');
        try {
          const questionList = await deepSearchQuestionsMutation.mutateAsync(message);

          if (questionList.length > 0) {
            setQuestionsWithSuggestions(questionList);
            setCurrentQuestionIndex(0);
            setCurrentSuggestions(questionList[0].suggestions || []);
            pushSystemMessage(questionList[0].question);
            return;
          }
          pushSystemMessage("I couldn't generate any questions about this topic. Please try a different topic.");
        } catch (error) {
          pushSystemMessage('Sorry, there was an error generating questions. Please try again.');
        }
        return;
      }

      if (currentQuestionIndex >= 0 && currentQuestionIndex < questionsWithSuggestions.length) {
        setAnswers((prev) => ({
          ...prev,
          [currentQuestionIndex]: message,
        }));

        const nextIndex = currentQuestionIndex + 1;
        if (nextIndex < questionsWithSuggestions.length) {
          setCurrentQuestionIndex(nextIndex);
          setCurrentSuggestions(questionsWithSuggestions[nextIndex].suggestions || []);
          pushSystemMessage(questionsWithSuggestions[nextIndex].question);
          return;
        }

        const questions = questionsWithSuggestions.map((q) => q.question);

        setCurrentSuggestions([]);
        const finalAnswers = { ...answers, [currentQuestionIndex]: message };
        onComplete({ topic, questions, answers: finalAnswers });
        return;
      }

      pushSystemMessage("I'm not sure how to respond to that. Please try again.");
    },
    [
      currentQuestionIndex,
      deepSearchQuestionsMutation,
      pushLoadingState,
      pushSystemMessage,
      questionsWithSuggestions,
      topic,
      answers,
      onComplete,
    ],
  );

  const resetQuestions = useCallback(() => {
    setTopic(undefined);
    setQuestionsWithSuggestions([]);
    setCurrentQuestionIndex(-1);
    setAnswers({});
    setCurrentSuggestions([]);
    isInitialized.current = false;
  }, []);

  useEffect(() => {
    if (!isInitialized.current) {
      resetQuestions();
    }
    isInitialized.current = true;
  }, [resetQuestions]);

  return {
    placeholder,
    handleUserMessage,
    resetQuestions,
    currentSuggestions,
  };
};
